import { useCallback, useEffect, useMemo, useState } from 'react';

import { useAtomValue } from 'jotai';
import {
  type GetReferralsMetricsParams,
  useReferrals,
  useReferralsMetrics,
} from '../../../react-query/useReferralsList';
import {
  referralsCustomDateRangeAtom,
  referralsSearchAtom,
  referralsSelectedDateRangeAtom,
  referralsSelectedLocationAtom,
  referralsSelectedSpecialtyAtom,
  referralsSelectedStatusAtom,
} from '../atoms';
import type { ReferralFilters } from '../referralPageTypes';

export const useReferralsData = () => {
  // Pagination and sorting state
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] =
    useState<ReferralFilters['sort_by']>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [lastAction, setLastAction] = useState<'sort' | 'filter' | null>(null);

  // Use referrals-specific search atom
  const searchQuery = useAtomValue(referralsSearchAtom);

  // Get filter state from atoms
  const selectedSpecialty = useAtomValue(referralsSelectedSpecialtyAtom);
  const selectedStatus = useAtomValue(referralsSelectedStatusAtom);
  const selectedLocation = useAtomValue(referralsSelectedLocationAtom);
  const selectedDateRange = useAtomValue(referralsSelectedDateRangeAtom);
  const customDateRange = useAtomValue(referralsCustomDateRangeAtom);

  // Watch for search changes and trigger refetch
  useEffect(() => {
    setCurrentPage(1);
    setLastAction('filter');
  }, [searchQuery]);

  // Watch for filter changes and trigger refetch
  useEffect(() => {
    setCurrentPage(1);
    setLastAction('filter');
  }, [selectedSpecialty, selectedStatus, selectedLocation, selectedDateRange, customDateRange]);

  // Build API query parameters
  const queryParams = useMemo(() => {
    const params: ReferralFilters = {
      page: currentPage,
      limit: 20,
      sort_by: sortField,
      sort_order: sortDirection,
    };

    // Add filters
    if (selectedSpecialty !== 'all') {
      params.specialty_id = parseInt(selectedSpecialty, 10);
    }
    if (selectedStatus !== 'all') {
      params.status = selectedStatus;
    }
    if (selectedLocation !== 'all') {
      params.destination_practice_id = selectedLocation;
    }
    if (selectedDateRange !== 'all') {
      if (selectedDateRange === 'custom') {
        // Only use custom date range if both dates are provided
        if (customDateRange?.from && customDateRange?.to) {
          params.start_date = customDateRange.from.toISOString().split('T')[0];
          params.end_date = customDateRange.to.toISOString().split('T')[0];
        }
        // If custom date range is incomplete, don't send any date parameters
      } else {
        // Use predefined date range
        params.date_range = selectedDateRange;
      }
    }
    if (searchQuery.trim()) {
      params.search = searchQuery.trim();
    }

    return params;
  }, [
    currentPage,
    sortField,
    sortDirection,
    selectedSpecialty,
    selectedStatus,
    selectedLocation,
    selectedDateRange,
    customDateRange,
    searchQuery,
  ]);

  // API calls
  const {
    data: referralsData,
    isLoading: isReferralsLoading,
    isRefetching: isReferralsRefetching,
    refetch: refetchReferrals,
    error: referralsError,
  } = useReferrals(queryParams);

  // Build metrics query parameters (same filters as referrals, but without pagination/sorting)
  const metricsParams = useMemo(() => {
    const params: GetReferralsMetricsParams = {};

    // Add filters (only supported ones for metrics API)
    if (selectedSpecialty !== 'all') {
      params.specialty_id = parseInt(selectedSpecialty, 10);
    }
    if (selectedLocation !== 'all') {
      params.destination_practice_id = selectedLocation;
    }
    if (selectedDateRange !== 'all') {
      if (selectedDateRange === 'custom') {
        // Only use custom date range if both dates are provided
        if (customDateRange?.from && customDateRange?.to) {
          params.start_date = customDateRange.from.toISOString().split('T')[0];
          params.end_date = customDateRange.to.toISOString().split('T')[0];
        }
        // If custom date range is incomplete, don't send any date parameters
      } else {
        // Use predefined date range
        params.date_range = selectedDateRange;
      }
    }
    // Note: status and search filters are not supported in metrics API

    return params;
  }, [selectedSpecialty, selectedLocation, selectedDateRange, customDateRange]);

  const { data: metricsData } = useReferralsMetrics(metricsParams);

  // Derived state
  const referrals = referralsData?.referrals || [];
  const metrics = metricsData?.metrics || {
    total: 0,
    submitted: 0,
    needsReferral: 0,
    failed: 0,
  };
  const hasError = !!referralsError;
  const isInitialLoading = isReferralsLoading && !referralsData;

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handleSort = useCallback((field: string, direction: 'asc' | 'desc') => {
    setSortField(field as ReferralFilters['sort_by']);
    setSortDirection(direction);
    setLastAction('sort');
  }, []);

  // Table sort field mapping - now column keys match API sort_by values directly
  const tableSortField = useMemo(() => {
    return sortField;
  }, [sortField]);

  return {
    // Data
    referrals,
    metrics,
    pagination: referralsData?.pagination,
    hasError,
    isInitialLoading,
    isReferralsRefetching,
    lastAction,

    handlePageChange,
    handleSort,
    refetchReferrals,

    // Table props
    tableSortField,
    sortDirection,
  };
};
